import { useLazyQuery, useQuery } from '@apollo/client'
import ProductCategories from '@deltek/specification-client/core/Constants/productCategories'
import ProductListingLevels from '@deltek/specification-client/core/Constants/productListingLevels'
import { AppHeaderContext } from '@deltek/specification-client/core/Contexts/AppHeaderContext'
import { CurrentUserInfoQuery } from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import { CurrentUserInfoResponse } from '@deltek/specification-client/core/Models/currentUserInfo'
import {
    ListingMatch,
    ListingSearchInput,
    LogEventSource,
} from '@deltek/specification-client/core/Models/listingSearch'
import { ProductTypeSelectionInput } from '@deltek/specification-client/core/Models/productType'
import { SearchFacet } from '@deltek/specification-client/core/Models/searchFacet'
import { SearchFacetInput } from '@deltek/specification-client/core/Models/searchFacetInput'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { summarizeApolloError } from '@deltek/specification-client/core/Utilities/apolloUtils'
import { NormalizedProductType } from '@deltek/specification-client/core/Widgets/ProductListing/model'
import {
    escapeElementDelimiter,
    isUserBPMAdmin,
    sortList,
} from '@deltek/specification-client/core/Widgets/ProductListing/productsUtils'
import {
    getInputSelectionValuesFromQueryString,
    getSelectedValuesFromQueryString,
} from 'bpm/pages/ProductsPage/productsUtils'
import clsx from 'clsx'
import isEqual from 'lodash.isequal'
import React, {
    MutableRefObject,
    memo,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { IdType } from 'react-table'
import AutoSizer from 'react-virtualized-auto-sizer'
import { FixedSizeGrid } from 'react-window'

import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import {
    ContentLibrary,
    ListingLevel,
} from '@deltek/specification-client/core/Models/product'
import { Button } from '@material-ui/core'
import { FirmNameQuery } from 'bpm/api/graphQL/queries/firm.queries'
import { ListingsGetListingProductTypeProperties } from 'bpm/api/graphQL/queries/product.queries'
import { ProductSearchAutosuggestQuery } from 'bpm/api/graphQL/queries/productSearch.queries'
import {
    ProductPropertyDetailResponse,
    ProductSearchAutosuggestResponse,
} from 'bpm/api/graphQL/types/product.types'
import ProductSearchBar from 'bpm/components/Filter/ProductSearchBar'
import SpecTableTitleSettings from 'bpm/components/SpecTable/SpecTableTitleSettings'
import { SpecColumn } from 'bpm/components/SpecTable/Spectable.types'
import { IdTitleProps } from 'bpm/components/SpecTable/settings/TableSettings'
import { useTableSettingsStore } from 'bpm/components/SpecTable/settings/store'
import LoadingBackdrop from 'bpm/components/loadingBackdrop/LoadingBackdrop'
import { noteTypes } from 'bpm/pages/ProductsPage/ProductDetailsPage/components/ListingNotes/ListingNotes.constants'
import { resetPDNStore } from 'bpm/pages/ProductsPage/ProductDetailsPage/components/ListingNotes/ListingNotes.store'
import {
    NoteType,
    NoteTypeReasonOption,
} from 'bpm/pages/ProductsPage/ProductDetailsPage/components/ListingNotes/ListingNotes.types'
import ListingNotesModal from 'bpm/pages/ProductsPage/ProductDetailsPage/components/ListingNotes/ListingNotesModal'
import usePDNhook from 'bpm/pages/ProductsPage/ProductDetailsPage/components/ListingNotes/hooks/usePDNHook'
import { useNoteFiltersStore } from 'bpm/pages/ProductsPage/ProductDetailsPage/components/ListingNotes/store/filtersStore'
import { useProductListingStyle } from 'bpm/pages/ProductsPage/ProductListingsPage/ProductLisitngsPage.styles'
import AccordionWindow from 'bpm/pages/ProductsPage/ProductListingsPage/ProductListingGroupingTree'
import { ProductListingResultGrid } from 'bpm/pages/ProductsPage/ProductListingsPage/components/ProductListingResultGrid'
import {
    createFilter,
    getFacetInputsFromFilterSelection,
    getFilterDisabledValues,
    getSelectedProductTypesFromQueryString,
    isAdditionalContentSelected,
    isDeltekUserorAdmin,
    updateQueryString,
} from 'bpm/pages/ProductsPage/productsUtils'
import { useNoteReasonsStore } from 'bpm/pages/ProductsPage/store'
import TablePage from 'bpm/pages/TablePage'
import ProductHeader from 'bpm/pages/components/ProductHeader'
import { processEnv } from 'components/configuration/environmentVariables'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import {
    adminColumnKeys,
    adminColumns,
    columnMap,
    columns,
    defaultColumns,
} from '../../TablePage/tablePage'
import { TVColumn, TVData } from '../ProductCardManagementPage.types'
import ResultsExport from '../ResultsExport'
import { useListingPageStore } from '../store'
import BulkCopyMoveDialog from './BulkCopyMoveDialog'
import BulkProgressStatusDialog from './BulkProgressStatusDialog'
import tableCell from './CellRenderers/tableCell'
import tableFilters from './CellRenderers/tableFilters'
import tableFormattedText from './CellRenderers/tableFormattedText'
import ProductListingLoading from './ProductListingLoading'
import {
    CurrentDropDownInput,
    OPTIMISTIC_CARDS,
    PER_PAGE,
    defaultQuery,
    defaultSortOrder,
    defaultfilterParam,
    emptyInputSelectionObject,
    emptySelectionObject,
    facetIdMap,
    validFacets,
    validOrderBy,
} from './ProductListingsPage.constants'
import useListingQueryHelper from './useListingQueryHelper'

const ProductListingsPage: React.FC = () => {
    const infiniteLoaderRef = useRef(null)
    const hasMountedRef = useRef(false)
    const sortRef = useRef<string>('')
    const tableRef = useRef<any>(undefined)
    const accordionTreeRef = useRef<HTMLDivElement>(null)

    const prevSelectedAdditionalContent: MutableRefObject<string[]> = useRef([])
    const prevSelectedInputValuesRef = useRef<Record<string, Date | string | null> | undefined>(undefined)

    // #region Note Reason utils
    const reasons = useNoteReasonsStore((state) => state.reasons)

    const pdnTypes = useMemo(() => {
        const reasonsOrdered = {
            GENERALNOTE: reasons.GENERALNOTE,
            COPYMOVE: reasons.COPYMOVE,
            LISTINGTYPECHANGE: reasons.LISTINGTYPECHANGE,
            DELETEORSTATUSCHANGE: reasons.DELETEORSTATUSCHANGE,
        } as Record<NoteType, string[]>
        if (!Object.entries(reasonsOrdered)) return []
        return Object.entries(reasonsOrdered).flatMap(([key, value]) =>
            value
                ?.sort((a, b) => {
                    if (a > b) {
                        return 1
                    }
                    if (a < b) {
                        return -1
                    }
                    return 0
                })
                .map(
                    (val) =>
                    ({
                        noteType: noteTypes[key as keyof typeof noteTypes],
                        reason: val,
                    } as NoteTypeReasonOption)
                )
        )
    }, [reasons])

    const getNoteTypeReasonOptionFromString = useCallback(
        (value: string) => {
            const splitValue = value.split('-')
            const noteType = splitValue[0]
            const reason = splitValue[1]
            return pdnTypes?.find(
                (reasons) =>
                    reasons?.reason === reason &&
                    reasons?.noteType ===
                    noteTypes[noteType as keyof typeof noteTypes]
            ) as NoteTypeReasonOption
        },
        [pdnTypes]
    )
    // #endregion

    const normalizeFacets = useCallback(
        (facets?: Partial<SearchFacetInput>[]) => {
            // Use this function to remove duplicate facets.
            // Note: If there's a duplicate, the prior will be overwritten by succeeding one.
            return facets
                ? Object.entries(
                    facets.reduce(
                        (
                            acc: Record<string, string[]>,
                            facet: Partial<SearchFacetInput>
                        ) => ({
                            ...acc,
                            [facet.name as string]: facet.values ?? [],
                        }),
                        {}
                    )
                )
                    .map(
                        ([key, value]) =>
                        ({ name: key, values: value } as Partial<
                            SearchFacetInput
                        >)
                    )
                    .filter((facet) => !!facet.values?.length)
                : []
        },
        []
    )

    const renderColumn = useCallback((column) => {
        let tempColumn = { ...column }

        if (!!tableFilters[column.id]) {
            tempColumn.Filter = tableFilters[column.id]
        }

        if (!!tableFormattedText[column.id]) {
            tempColumn.formattedText = tableFormattedText[column.id]
        }

        if (!!tableCell[column.id]) {
            tempColumn.Cell = tableCell[column.id]
        }

        if (column.id === 'hasProductDetailNotes')
            return {
                ...column,
                Cell: NotesCell,
                Filter: tableFilters[column.id],
            }

        return tempColumn
    }, [])

    const {
        updateAppHeaderContent,
        updateAppHeaderStyles,
        hasLowerBottomToolbar,
    } = useContext(AppHeaderContext)
    const hasNextPage = useListingPageStore((state) => state.hasNextPage)
    const setHasNextPage = useListingPageStore((state) => state.setHasNextPage)
    const isBulkActionsButtonEnabled = useListingPageStore(
        (state) => state.enableBulkActions
    )
    const copyMoveParams = useListingPageStore((state) => state.copyMoveParams)
    const filterValues = useListingPageStore((state) => state.filterValues)
    const setFilterValues = useListingPageStore(
        (state) => state.setFilterValues
    )

    const noteTypesFilter = useNoteFiltersStore((state) => state.noteTypes)
    const setNoteTypes = useNoteFiltersStore((state) => state.setNoteTypes)

    const { showGlobalSnackbar } = useGlobalSnackbar()
    const history = useHistory()
    const location = useLocation()

    const { data: currentUserInfoResponse } = useQuery<CurrentUserInfoResponse>(
        CurrentUserInfoQuery
    )

    const {
        isBPM,
        isDeltekUser,
        isDeltekAdmin,
        isAdminAccess,
        isBPMReviewer,
    } = useMemo(() => {
        const {
            isDeltekUser,
            isDeltekAdmin,
            isDeltekPublishingAuthor,
            isDeltekAuthor,
            isDeltekReviewer,
        } = isDeltekUserorAdmin(currentUserInfoResponse)
        const { isBPM, isBPMReviewer } = isUserBPMAdmin(currentUserInfoResponse)

        return {
            isBPM,
            isBPMReviewer,
            isDeltekUser,
            isDeltekAdmin,
            isAdminAccess:
                isDeltekAdmin ||
                isDeltekPublishingAuthor ||
                isDeltekAuthor ||
                isDeltekReviewer,
        }
    }, [currentUserInfoResponse])

    const [isNotesDialogOpen, setIsNotesDialogOpen] = useState(false)

    const [listingLevel, setListingLevel] = useState<ListingLevel>()

    const [isBulkCopyMoveOpen, setIsBulkCopyMoveOpen] = useState(false)

    const accountType =
        currentUserInfoResponse?.currentUserInfo.account.accountType
    const accountId = currentUserInfoResponse?.currentUserInfo.account.accountId

    const [listingMatchDataResults, setListingMatchDataResults] = useState<
        Partial<ListingMatch>[] | undefined
    >()

    const [selectedValues, setSelectedValues] = useState<
        Record<string, string[]> | undefined
    >({
        ...emptySelectionObject,
        ...getSelectedValuesFromQueryString(location.search),
    })

    const [selectedInputValues, setSelectedInputValues] = useState<
        Record<string, Date | string | null> | undefined
    >({
        ...emptyInputSelectionObject,
        ...getInputSelectionValuesFromQueryString(location.search),
    })

    const [facets, setFacets] = useState<SearchFacetInput[]>([
        ...getFacetInputsFromFilterSelection(selectedValues),
    ])

    const [productTypeSelections, setProductTypeSelections] = useState<
        NormalizedProductType[]
    >([...getSelectedProductTypesFromQueryString(location.search)])

    const [currentDropDownName, setCurrentDropDownName] = useState<string>('')
    const [currentDropDownInput, setCurrentDropDownInput] = useState<
        CurrentDropDownInput
    >({ filterName: '', inputValue: '' })

    const [scrollState, setScrollState] = useState({
        rowIndex: 0,
        columnIndex: 0,
    })

    const [columnCount, setColumnCount] = useState<number>(0)
    const [refetch, setRefetch] = useState<boolean>(false)
    const [currentTotal, setCurrentTotal] = useState<number>(PER_PAGE)
    const [searchFetchMoreLoading, setSearchFetchMoreLoading] = useState<
        boolean
    >(false)
    const [tableFilterView, setTableFilterView] = useState<boolean>(true)
    const [bulkActionType, setBulkActionType] = useState('Copy')
    const [defaultExpandedRows, setDefaultExpandedRows] = useState<
        Record<string, boolean> | undefined
    >()
    const [autoResetExpanded, setAutoResetExpanded] = useState(false)
    const classes = useProductListingStyle({ hasLowerBottomToolbar })

    const [
        isPreviousTableSettingsLoaded,
        setIsPreviousTableSettingsLoaded,
    ] = useState(false)

    const {
        searchData,
        getListings,
        searchFetchMore,
        listingLoading,
        listingNetworkStatus,
        searchLoading,
        setSearchLoading,
        showBottomLoadingIndicator,
        fetchTableSettings,
        setShowTableLoadingIndicator,
        showTableLoadingIndicator,
        saveTableViewSettings,
    } = useListingQueryHelper()

    const { fetchNotes, pdnLoading } = usePDNhook()

    const NotesCell = memo(({ row }: TVData) => {
        const classes = useProductListingStyle({})
        const value = row.original?.hasProductDetailNotes
        const listingId = row.original?.listingId
        const listingType = row.original?.type
        const notes = row.original?.noteReasons
        const handleNotesClick = () => {
            fetchNotes(listingId)
            setListingLevel(listingType)

            const notesOpenTimeout = setTimeout(() => {
                setIsNotesDialogOpen(true)
                clearTimeout(notesOpenTimeout)
            }, 0)
        }

        const notesText = notes?.some(Boolean)
            ? `Note - ${getNoteTypeReasonOptionFromString(notes?.[0])?.reason}`
            : 'Note'

        if (value === true || value === false) {
            return (
                <HoverTooltip text={value ? notesText : ''}>
                    <Button
                        className={classes.notesButton}
                        onClick={handleNotesClick}
                        disableRipple={true}
                        aria-label="Notes"
                    >
                        {value ? notesText : ''}
                    </Button>
                </HoverTooltip>
            )
        } else {
            return <></>
        }
    })

    const newColumns = useMemo(() => {
        const newColumnMap = !!isAdminAccess ? adminColumns : columns

        if (isBPMReviewer) {
            newColumnMap.filter((col) => col.id !== 'isActive')
        }

        return newColumnMap.map((column) => {
            return renderColumn(column)
        })
    }, [history, selectedValues, fetchNotes, isAdminAccess, isBPMReviewer])

    const newDefaultColumns = useMemo(() => {
        let tempColumns = defaultColumns.map((column) => {
            return renderColumn(column)
        })

        if (isBPMReviewer) {
            tempColumns = tempColumns.filter((col) => col.id !== 'isActive')
        }

        return tempColumns
    }, [history, isBPMReviewer])

    const [visibleColumns, setVisibleColumns] = useState([...newDefaultColumns])

    const [hiddenColumns, setHiddenColumns] = useState(
        newColumns.map((col: TVColumn) => col?.id)
    )

    const tableGrouping = useTableSettingsStore((state) => state.groupColumns)
    const setIsTableView = useTableSettingsStore(
        (state) => state.setIsTableView
    )
    const isTableView = useTableSettingsStore((state) => state.isTableView)
    const selectedColumnsState = useTableSettingsStore(
        (state) => state.selectedColumns
    )
    const collapseColumnCheckedState = useTableSettingsStore(
        (state) => state.collapseColumnChecked
    )
    const isTableSettingsLoaded = useTableSettingsStore(
        (state) => state.isTableSettingLoaded
    )

    const columnWidthsState = useTableSettingsStore(
        (state) => state.columnWidths
    )
    const orderByState = useTableSettingsStore((state) => state.orderBy)
    const filtersState = useTableSettingsStore((state) => state.filters)

    const setCollapseColumnChecked = useTableSettingsStore(
        (state) => state.setCollapseColumnChecked
    )
    const setPinningProps = useTableSettingsStore(
        (state) => state.setPinningProps
    )
    const setSelectedRowIds = useTableSettingsStore(
        (state) => state.setSelectedRowIds
    )
    const setEnableBulkActions = useListingPageStore(
        (state) => state.setEnableBulkActions
    )
    const setCopyMoveParams = useListingPageStore(
        (state) => state.setCopyMoveParams
    )

    const { memoizedOrderByString } = useMemo(() => {
        return {
            memoizedOrderBy: orderByState
                .split(',')
                .reduce((acc: Record<string, boolean>, curr: string) => {
                    const sort = curr.split(' ')
                    acc[sort[0]] = !!sort[1]
                    return acc
                }, {}),
            memoizedOrderByString: orderByState,
        }
    }, [orderByState])

    const { memoizedTableGrouping, memoizedGroupIds } = useMemo(
        () => ({
            memoizedTableGrouping: tableGrouping ?? [],
            memoizedGroupIds: (tableGrouping ?? []).map(
                (group: IdTitleProps) => group.id
            ),
        }),
        [tableGrouping]
    )

    const prevOrderBy = useRef(memoizedOrderByString)
    const prevFilters = useRef(filtersState)

    const [
        manufacturerProductSearchInput,
        setManufacturerProductSearchInput,
    ] = useState<Partial<ListingSearchInput>>({
        skip: 0,
        top: PER_PAGE,
        orderBy: defaultSortOrder,
        logEventSource: LogEventSource.BpmSearchListings,
    })

    const prevOrderBySearchRef: MutableRefObject<string | undefined> = useRef(
        undefined
    )

    const prevFacetsSearchRef: MutableRefObject<
        Partial<SearchFacetInput>[] | undefined
    > = useRef(undefined)

    const prevProductTypeSelectionsRef: MutableRefObject<
        NormalizedProductType[]
    > = useRef([])

    const [productCount, setProductCount] = useState(0)

    const [onFilterSelected, setOnFilterSelected] = useState(false)

    const [resultFacetLookup, setResultFacetLookup] = useState<
        Record<string, string[]>
    >({})

    const [skip, setSkip] = useState(PER_PAGE)

    const [groupBy, setGroupBy] = useState<Record<string, boolean | string>>({})
    const [cardGroupBy, setCardGroupBy] = useState({})

    const tableColumns = useMemo(() => [...newDefaultColumns, ...newColumns], [
        newDefaultColumns,
    ])

    const disabledFilterValues = useMemo(() => {
        const hasManufacturerOptions = !!selectedValues?.withManufacturerOption?.some(
            (value) => value === 'true'
        )
        return getFilterDisabledValues(
            resultFacetLookup,
            hasManufacturerOptions
        )
    }, [selectedValues, resultFacetLookup, getFilterDisabledValues])

    const effectiveSortString = useMemo(() => {
        // In card view, use URL sorting (selectedValues.orderBy), in table view use store sorting (memoizedOrderByString)
        if (!isTableView && selectedValues?.orderBy?.length) {
            let cardViewSort: string[] = []
            // Needed to inverse the sorting of orderby when on card view since it was sorted by relevance and not alphabetical
            selectedValues.orderBy.forEach((value) => {
                if (value === "type") {
                    cardViewSort.push("type desc")
                }
                else if (value === "type desc") {
                    cardViewSort.push("type")
                }
                else {
                    cardViewSort.push(value)
                }
            })
            return cardViewSort.join(',')
        }
        return memoizedOrderByString
    }, [isTableView, selectedValues?.orderBy, memoizedOrderByString])

    const sortedProductList = useMemo(() => {
        return sortList(
            [...(listingMatchDataResults ?? [])],
            effectiveSortString
        )
    }, [listingMatchDataResults, effectiveSortString, visibleColumns])

    //#region graphql mutation and queries
    const [getFirmNameInfo, { data: currentFirmNameInfo }] = useLazyQuery<any>(
        FirmNameQuery,
        { fetchPolicy: 'no-cache', nextFetchPolicy: 'no-cache' }
    )

    const assignedCompany = escapeElementDelimiter(
        `'${currentFirmNameInfo?.firm?.displayName}'%|%'${currentFirmNameInfo?.firm?.name}'`
    )

    const { loading: autosuggestLoading, data: autosuggestData } = useQuery<
        ProductSearchAutosuggestResponse
    >(ProductSearchAutosuggestQuery, {
        variables: {
            input: {
                facets: createFilter(
                    facets,
                    'AutoSuggest',
                    currentDropDownName
                ).filter(
                    (facet) => facet.name !== currentDropDownName
                    // don't limit autosuggest by what's already selected in same filter
                ),
                searchLimit: 50,
                searchField: currentDropDownName,
                searchText: currentDropDownInput.inputValue,
                productTypeSelections:
                    currentDropDownName !== 'sectionNameWithProductType'
                        ? productTypeSelections.map(
                            (each) =>
                            ({
                                productTypeProperties:
                                    each.productTypeProperties,
                                sectionName: each.sectionName,
                                productTypeName: each.productTypeName,
                            } as ProductTypeSelectionInput)
                        )
                        : [],
            },
        },
        skip: !currentDropDownName, // only run query if an autosuggest drop-down is open
        fetchPolicy: 'no-cache',
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error on product autosuggest',
                    variant: 'error',
                    id: `ProductListingsPage_ProductAutosuggestError`,
                },
            })
        },
    })

    const [
        getListingProductTypeProperties,
        {
            loading: loadingSelectedProductType,
            error: errorSelectedProductType,
            data: dataSelectedProductType,
        },
    ] = useLazyQuery<ProductPropertyDetailResponse>(
        ListingsGetListingProductTypeProperties,
        {
            displayName: 'GetListingProductTypeProperties',
            onError: (error) => {
                showGlobalSnackbar({
                    variables: {
                        message: summarizeApolloError(
                            error,
                            'Error fetching product type properties.'
                        ),
                        variant: 'error',
                        id:
                            'ProductTypeFilter_ListingProductTypePropertiesError',
                    },
                })
            },
            onCompleted: () => setOnFilterSelected(true),
        }
    )
    //#endregion

    const isItemLoaded = (index: number) => !!listingMatchDataResults?.[index]

    //#endregion CardView_Callback_Functions
    const setScrollRowAndColum = useCallback(
        (rowIndex: number, columnIndex: number) => {
            setScrollState({ rowIndex, columnIndex })
        },
        []
    )

    const handleAutoSuggestDropdown = (value: string) => {
        setCurrentDropDownName(value)
        setFilterValues({
            ...filterValues,
            sectionNames: [],
            sectionNameWithProductType: [],
            displayNameWithFirmName: [],
        })
    }

    const handleClearAll = () => {
        const search = `?${defaultQuery}`

        history.replace({
            ...history.location,
            search,
        })

        setSelectedInputValues({
            ...emptyInputSelectionObject,
            ...getInputSelectionValuesFromQueryString(search),
        })

        const selectedPTFromQuery = getSelectedProductTypesFromQueryString(
            search
        )

        prevProductTypeSelectionsRef.current = selectedPTFromQuery
        setProductTypeSelections([...selectedPTFromQuery])

        const updatedSelectedValues = {
            ...emptySelectionObject,
            ...getSelectedValuesFromQueryString(search),
        }

        setSelectedValues(updatedSelectedValues)
        setFacets([...getFacetInputsFromFilterSelection(updatedSelectedValues)])
        setNoteTypes([])
    }

    const onFilterSelectionChangeHandler = useCallback(
        (selection: Record<string, string[]> | undefined) => {
            const selectedData: Record<string, string[]> = {
                ...selection,
            }

            if (
                selectedData.orderBy?.length === 2 &&
                !isEqual(selectedData.orderBy, selectedValues?.orderBy)
            ) {
                const groupByDataObj = JSON.parse(selectedData.orderBy[0])
                const sortByDataObj = JSON.parse(selectedData.orderBy[1])
                const orderByData = [...sortByDataObj].filter(
                    (fieldName) => !!fieldName
                ) as string[]
                setGroupBy(groupByDataObj)
                selectedData.orderBy = orderByData
            } else if (
                selectedData.orderBy?.length !== 2 &&
                Object.keys(groupBy).length !== 0
            )
                setGroupBy({})
            else if (Object.values(groupBy).includes('spLoadMore'))
                setGroupBy({ ...lastGroupingInfoRef.current })

            setSelectedValues(selectedData)
            setFacets(getFacetInputsFromFilterSelection(selectedData))
            updateQueryString(selectedData, undefined, history, location)
            setAutoResetExpanded(false)
        },
        [
            groupBy,
            history,
            location,
            selectedValues,
            noteTypesFilter,
            setNoteTypes,
        ]
    )

    const handleFilterInputChangeDebounced = useCallback(
        (filterName, inputValue) => {
            setCurrentDropDownInput({
                filterName,
                inputValue,
            })
        },
        []
    )

    const handleEmptyInputSelection = useCallback(
        (selection) => {
            setSelectedInputValues(selection)
            updateQueryString(undefined, selection, history, location)
        },
        [history, location]
    )

    const handleProductTypeSelection = useCallback(
        (values: NormalizedProductType[]) => {
            const propArr: string[] = []
            values.forEach((prop) => {
                if (!!prop.productTypeProperties?.length) {
                    prop.productTypeProperties?.forEach(
                        (ptp: NormalizedProductType) =>
                            propArr.push(
                                `${prop.sectionName}%|%${prop.productTypeName
                                }%|%${ptp.propertyName
                                }:${ptp.propertyOptionIds?.join(',')}`
                            )
                    )
                } else {
                    propArr.push(
                        `${prop.sectionName}%|%${prop.productTypeName}`
                    )
                }
            })

            const productTypePropertiesQuery =
                (propArr.length && encodeURIComponent(propArr.join(';'))) || ''

            const searchParams = new URLSearchParams(location.search)
            searchParams.has('ptp') && searchParams.delete('ptp')
            if (productTypePropertiesQuery) {
                searchParams.set('ptp', productTypePropertiesQuery)
            }
            if (values.length > 0 && searchParams.has('sp')) {
                history.replace({
                    ...location,
                    search: searchParams.toString(),
                })
            }
            if (!isEqual(values, productTypeSelections)) {
                setProductTypeSelections(values)
            }
        },
        [history, location, location, productTypeSelections]
    )

    const handleCardViewFilterClick = useCallback((showFilters: boolean) => {
        setTableFilterView(showFilters)
    }, [])

    const handleViewMenuChange = useCallback(
        (event: React.ChangeEvent<{ value: unknown }>) => {
            let defaultPath = `/bpm/products/${event.target.value}`
            if (event.target.value === 'all')
                defaultPath = `/bpm/products/${event.target.value}${location.search}`
            history.push(defaultPath)
        },
        [history, location]
    )

    const handleCompanyRoute = useCallback(() => {
        history.push(
            `/bpm/products/company/${currentUserInfoResponse?.currentUserInfo.account.accountId}`
        )
    }, [currentUserInfoResponse, history])
    //#endregion CardView_Callback_Functions

    //#region Table_Callback_Functions

    const handleBulkActions = useCallback(
        (action: string) => {
            switch (action) {
                case 'copy':
                    setIsBulkCopyMoveOpen(true)
                    setBulkActionType('Copy')
                    return
                case 'move':
                    setIsBulkCopyMoveOpen(true)
                    setBulkActionType('Move')
                    return
                case 'delete':
                    setBulkActionType('Delete')
                    return
                default:
                    return
            }
        },
        [copyMoveParams]
    )

    const getExpandedRows = useCallback(
        (
            listings: Partial<ListingMatch>[] | undefined,
            collapseGroupState: Record<string, boolean>
        ) => {
            const expandedRows: Record<string, boolean> = {}
            if (!!collapseGroupState) {
                const collapsedStates = Object.entries(collapseGroupState)
                listings?.forEach((listing) => {
                    for (var i = 0; i < collapsedStates.length; i++) {
                        const [firstGroupedColumnId, value] = collapsedStates[0]
                        let key: string
                        if (i === 0) {
                            let key = `${firstGroupedColumnId}:${listing[
                                firstGroupedColumnId as keyof ListingMatch
                            ]
                                }`
                            expandedRows[key] = !value
                        }

                        if (i === 1) {
                            const [
                                secondGroupedColumnId,
                                value,
                            ] = collapsedStates[i]
                            key = `${firstGroupedColumnId}:${listing[
                                firstGroupedColumnId as keyof ListingMatch
                            ]
                                }>${secondGroupedColumnId}:${listing[
                                secondGroupedColumnId as keyof ListingMatch
                                ]
                                }`
                            expandedRows[key] = !value
                        }
                        if (i === 2) {
                            const [secondGroupedColumnId] = collapsedStates[1]
                            const [
                                thirdGroupedColumnId,
                                value,
                            ] = collapsedStates[i]
                            key = `${firstGroupedColumnId}:${listing[
                                firstGroupedColumnId as keyof ListingMatch
                            ]
                                }>${secondGroupedColumnId}:${listing[
                                secondGroupedColumnId as keyof ListingMatch
                                ]
                                }>${thirdGroupedColumnId}:${listing[
                                thirdGroupedColumnId as keyof ListingMatch
                                ]
                                }`
                            expandedRows[key] = !value
                        }
                    }
                })
            }
            return expandedRows
        },
        []
    )

    const resetSelectedRowsThenRefetch = useCallback(() => {
        setRefetch(true)
        setAutoResetExpanded(true)
        tableRef?.current?.resetSelectedRows?.()
        setEnableBulkActions(false)
        setListingMatchDataResults([])
        setCopyMoveParams({})
    }, [])

    const resetFetch = useCallback(() => {
        setManufacturerProductSearchInput({
            facets: getFacetInputsFromFilterSelection(selectedValues),
            skip: 0,
            top: PER_PAGE,
            orderBy: defaultSortOrder,
            logEventSource: LogEventSource.BpmSearchListings,
        })

        setRefetch(true)
        setAutoResetExpanded(true)
    }, [selectedValues, getFacetInputsFromFilterSelection])

    const handleTableOrder = useCallback(
        (columnOrder: IdType<any>[]) => {
            const newColumns = tableColumns
                ?.filter((col) =>
                    columnOrder?.find((newCol) => newCol === col.id)
                )
                .map((column) => {
                    return {
                        ...column,
                        width: columnWidthsState?.[column.id ?? ''] ?? 300,
                        index: columnOrder?.findIndex(
                            (col) => col === column.id
                        ),
                    }
                })
                .sort((a, b) => a.index - b.index)

            let tempColumns = Array.from(
                new Set(newColumns.map((col) => JSON.stringify(col)))
            )
                .map((col) => JSON.parse(col))
                .map((column: TVColumn) => {
                    return renderColumn(column)
                })

            if (isBPMReviewer) {
                tempColumns.filter((col) => col.id !== 'isActive')
            }

            setVisibleColumns(tempColumns)
        },
        [
            tableColumns,
            columnWidthsState,
            selectedValues,
            fetchNotes,
            isBPMReviewer,
        ]
    )

    const handleColumnRemove = useCallback((hiddenColumns: IdType<any>[]) => {
        setHiddenColumns(hiddenColumns as string[])
    }, [])

    const handleCollapseColumnState = useCallback(
        (collapseColumnState: Record<string, boolean> | undefined) => {
            if (!!collapseColumnState) {
                setCollapseColumnChecked(collapseColumnState)
                const expandedRows = getExpandedRows(
                    listingMatchDataResults,
                    collapseColumnState
                )
                tableRef.current.handleToggleRowExpanded(expandedRows)
            }

            setAutoResetExpanded(false)
        },
        [listingMatchDataResults]
    )
    //#endregion Table_Callback_Functions

    //#region Common_Callback_Functions
    const composeFacets = useCallback(
        (pastFacets, isCardView?: boolean) => {
            let validFacetsFiltered = filtersState.filter((filter: any) => {
                if (
                    filter.id === 'hasManufacturerOptions' || // hasManufacturerOption is withManufacturerOption
                    filter.id === 'type' // type is listingLevels
                ) {
                    return true
                }
                return validFacets.includes(filter.id)
            })

            let composedFacets = pastFacets

            if (!isCardView) {
                composedFacets = [
                    ...(pastFacets ?? []).map(
                        (facet: Partial<SearchFacetInput>) => {
                            const newValueIndex = validFacetsFiltered.findIndex(
                                (validFacet: Partial<SearchFacetInput>) =>
                                    validFacet.id === facet.name ||
                                    (validFacet.id === 'type' &&
                                        facet.name === 'listingLevels') ||
                                    (validFacet.id ===
                                        'hasManufacturerOptions' &&
                                        facet.name === 'withManufacturerOption')
                            )

                            let newValue = validFacetsFiltered[newValueIndex]

                            if (newValue && facet.name === 'isActive') {
                                newValue = {
                                    id: 'isActive',
                                    value: [...newValue.value].map(
                                        (value: string) => {
                                            if (value === 'Inactive')
                                                return 'false'
                                            if (value === 'Active')
                                                return 'true'
                                            return value
                                        }
                                    ),
                                }
                            }

                            if (newValue && facet.name === 'contentLibrary') {
                                return {
                                    name: facet.name,
                                    values:
                                        Object.entries(ContentLibrary)
                                            .filter(([k, v]) =>
                                                newValue.value.includes(v)
                                            )
                                            .map(([k, v]) => k) ?? [],
                                }
                            }

                            if (newValueIndex > -1) {
                                validFacetsFiltered.splice(newValueIndex, 1)
                            }

                            if (newValue) {
                                return {
                                    ...facet,
                                    values: [
                                        ...new Set([
                                            ...(facet.values ?? []),
                                            ...(newValue.value ?? []),
                                        ]),
                                    ],
                                }
                            }
                            return facet
                        }
                    ),
                    ...validFacetsFiltered.map(
                        (facet: Record<string, string | string[]>) => {
                            let facetValue = facet.value

                            if (
                                facet.id === 'isActive' &&
                                !!facet.value.length
                            ) {
                                facetValue = (facet.value as string[]).map(
                                    (value: string) =>
                                        value === 'Inactive' ? 'false' : 'true'
                                )
                            }

                            if (
                                facet.id === 'hasManufacturerOptions' &&
                                !!facet.value.length
                            ) {
                                facetValue = (facet.value as string[]).map(
                                    (value: string) => value.toLowerCase()
                                )
                            }

                            if (
                                facet.id === 'contentLibrary' &&
                                !!facet.value.length
                            ) {
                                facetValue = (facet.value as string[]).map(
                                    (value: string) =>
                                        Object.entries(ContentLibrary)
                                            .filter(([k, v]) => v == value)
                                            .map(([k, v]) => k)[0] as string
                                )
                            }

                            return {
                                name: facetIdMap[facet.id as string],
                                values: facetValue,
                            }
                        }
                    ),
                ]
            }

            return composedFacets
        },
        [filtersState, facets, isTableView, filterValues]
    )

    const doRefetch = useCallback(() => {
        setShowTableLoadingIndicator(true)
        setIsPreviousTableSettingsLoaded(false)

        getListings({
            variables: {
                manufacturerProductSearchInput: {
                    ...manufacturerProductSearchInput,
                    facets: normalizeFacets(
                        manufacturerProductSearchInput?.facets
                    ),
                },
            },
        })

        prevFacetsSearchRef.current =
            manufacturerProductSearchInput?.facets ?? []

        prevOrderBySearchRef.current =
            manufacturerProductSearchInput?.orderBy ?? ''

        setRefetch(false)
        setSearchLoading(true)
    }, [getListings, manufacturerProductSearchInput])

    const handleLoadMore = useCallback(
        (_startIndex: number, stopIndex: number) => {
            if (
                !searchLoading &&
                !searchFetchMoreLoading &&
                !refetch &&
                searchData?.listingSearch?.count &&
                Math.ceil(currentTotal / columnCount) - stopIndex > 0 &&
                hasNextPage &&
                !!searchFetchMore
            ) {
                setSearchFetchMoreLoading(true)
                setCurrentTotal((prev) => prev + OPTIMISTIC_CARDS)

                return searchFetchMore({
                    variables: {
                        manufacturerProductSearchInput: {
                            ...manufacturerProductSearchInput,
                            skip: searchData.listingSearch.matches?.length,
                            top: PER_PAGE,
                        },
                    },
                })
            }
            return null
        },
        [
            manufacturerProductSearchInput,
            searchLoading,
            searchFetchMoreLoading,
            refetch,
            searchData,
            columnCount,
            currentTotal,
            hasNextPage,
            searchFetchMore,
        ]
    )
    const updateStateFromURL = useCallback(() => {
        const searchParams = new URLSearchParams(location.search)
        let selectedValuesUpdate = { ...selectedValues }
        if (
            Object.keys(groupBy).length === 0 &&
            location.search &&
            (searchParams.has('groupby') || searchParams.has('sortby'))
        ) {
            const groupByData =
                (searchParams.has('groupby') &&
                    decodeURIComponent(searchParams.get('groupby')!)) ||
                ''
            const sortBy =
                (searchParams.has('sortby') &&
                    decodeURIComponent(searchParams.get('sortby')!)) ||
                ''

            let groupByObj: Record<string, boolean> = {}
            groupByData
                ?.split(',')
                .forEach(
                    (group) =>
                        group?.split(' ')[0] &&
                        (groupByObj[group?.split(' ')[0]] =
                            group?.split(' ')[1] === 'true')
                )
            if (sortRef.current === sortBy && isEqual(groupByObj, groupBy))
                return
            if (!isEqual(groupByObj, groupBy)) {
                setGroupBy(groupByObj)
            }
            const currentOrderby = ((sortBy && sortBy.split(',')) || []).filter(
                (sort) => !!sort
            )

            if (!isEqual(selectedValues?.orderBy, currentOrderby)) {
                selectedValuesUpdate.orderBy = currentOrderby
                setSelectedValues({
                    ...selectedValues,
                    orderBy: currentOrderby,
                })
            }

            sortRef.current = sortBy
        }
    }, [location, selectedValues, groupBy])

    const purgeTVFilters = useCallback(() => {
        const selectedValues = {
            ...emptySelectionObject,
            ...getSelectedValuesFromQueryString(location.search),
        }

        setSelectedValues(selectedValues)

        setSelectedInputValues({
            ...emptyInputSelectionObject,
            ...getInputSelectionValuesFromQueryString(location.search),
        })

        setFacets([...getFacetInputsFromFilterSelection(selectedValues)])

        setProductTypeSelections([
            ...getSelectedProductTypesFromQueryString(location.search),
        ])
    }, [location.search])

    const composeQuery = useCallback(() => {
        let computedFacets = createFilter(facets, 'Search')

        const computedOrderBy = selectedValues?.orderBy?.length
            ? selectedValues.orderBy.join(',')
            : defaultSortOrder

        tableRef?.current?.resetSelectedRows?.()
        prevProductTypeSelectionsRef.current = productTypeSelections
        setManufacturerProductSearchInput((past) => ({
            ...past,
            orderBy: computedOrderBy,
            facets: composeFacets(computedFacets, true),
            lastUpdatedEndDate: selectedInputValues?.lastUpdatedEndDate
                ? selectedInputValues.lastUpdatedEndDate + 'T23:59:59.999Z'
                : undefined,
            lastUpdatedStartDate: selectedInputValues?.lastUpdatedStartDate
                ? selectedInputValues.lastUpdatedStartDate + 'T00:00:00.000Z'
                : undefined,
            lastUpdatedInPastDays: selectedInputValues?.lastUpdatedInPastDays
                ? 180
                : undefined,
            hasBimLinks: selectedValues
                ? isAdditionalContentSelected(
                    selectedValues['additionalContent'],
                    'hasBimLinks'
                )
                : undefined,
            hasCatalogs: selectedValues
                ? isAdditionalContentSelected(
                    selectedValues['additionalContent'],
                    'hasCatalogs'
                )
                : undefined,
            hasDocumentFL: selectedValues
                ? isAdditionalContentSelected(
                    selectedValues['additionalContent'],
                    'hasDocumentFL'
                )
                : undefined,
            hasDocumentSF: selectedValues
                ? isAdditionalContentSelected(
                    selectedValues['additionalContent'],
                    'hasDocumentSF'
                )
                : undefined,
            productTypeSelections: productTypeSelections.map(
                (each) =>
                ({
                    productTypeProperties: each.productTypeProperties,
                    sectionName: each.sectionName,
                    productTypeName: each.productTypeName,
                } as ProductTypeSelectionInput)
            ),
        }))
        setSkip(PER_PAGE)
        setRefetch(true)
        setCurrentTotal(10)
    }, [
        facets,
        selectedValues,
        productTypeSelections,
        selectedInputValues,
        location,
        isTableView,
    ])

    const handleViewToggle = useCallback(
        (value: boolean) => () => {
            setCardGroupBy(groupBy)
            setHasNextPage(
                searchData?.listingSearch?.count ?? 0 >= PER_PAGE
                    ? (listingMatchDataResults ?? []).length <
                    (searchData?.listingSearch?.count || 0)
                    : false
            )

            if (!value) {
                purgeTVFilters()
                composeQuery()
            } else {
                prevFilters.current = {}
            }

            setIsTableView(value, saveTableViewSettings)
        },
        [
            setIsTableView,
            groupBy,
            cardGroupBy,
            searchData?.listingSearch?.count,
            listingMatchDataResults,
            purgeTVFilters,
            composeQuery,
        ]
    )

    const [openExportModal, setOpenExportModal] = useState(false)

    const handleOpenExportModal = useCallback(() => {
        setOpenExportModal(true)
    }, [])
    const handleCloseExportModal = useCallback(() => {
        setOpenExportModal(false)
    }, [])

    const handleCloseNotesModal = useCallback(() => {
        setIsNotesDialogOpen(false)
        resetPDNStore()
    }, [])

    //#endregion Common_Callback_Functions

    //#region useEffects

    // this is for bpm access only
    useEffect(() => {
        const { isBPM } = isUserBPMAdmin(currentUserInfoResponse)
        const searchParams = new URLSearchParams(location.search)

        if (
            !isBPM ||
            searchParams.has(defaultfilterParam) ||
            !searchParams?.has('defaultFilter') ||
            !currentFirmNameInfo?.firm?.name
        )
            return

        searchParams.append(defaultfilterParam, assignedCompany)
        searchParams.delete('defaultFilter')
        history.replace({
            ...history.location,
            search: `?${searchParams.toString()}`,
        })

        const updatedSelectedValues = {
            ...selectedValues,
            ...getSelectedValuesFromQueryString(searchParams.toString()),
        }

        if (
            isEqual(
                facets,
                getFacetInputsFromFilterSelection(updatedSelectedValues)
            )
        )
            return

        setSelectedValues(updatedSelectedValues)
        setFacets([...getFacetInputsFromFilterSelection(updatedSelectedValues)])
    }, [currentFirmNameInfo, selectedValues, history, facets, isBPM])

    useEffect(() => {
        const searchFacets = createFacetLookup(
            searchData?.listingSearch.facets ?? []
        )

        if (searchData?.listingSearch.matches) {
            setProductCount(searchData?.listingSearch.count ?? 0)
            setResultFacetLookup(searchFacets)

            const listingMatchData =
                searchData?.listingSearch?.matches?.length === 0
                    ? []
                    : skip === PER_PAGE && !!searchData?.listingSearch?.count
                        ? [...searchData?.listingSearch?.matches]
                        : [
                            ...(listingMatchDataResults as Partial<
                                ListingMatch
                            >[]),
                            ...searchData?.listingSearch?.matches,
                        ]

            setListingMatchDataResults([
                ...[
                    ...new Set(
                        listingMatchData.map((listing) =>
                            JSON.stringify(listing)
                        )
                    ),
                ].map((listing) => ({
                    ...JSON.parse(listing),
                    noteReasons: selectedValues?.noteReasons,
                })),
            ])
        }
    }, [
        searchData,
        location,
        selectedValues,
        isTableView,
        skip,
        groupBy,
        selectedValues,
    ])

    useEffect(() => {
        updateAppHeaderStyles?.({
            titleTextColor: '#04325B',
            backgroundColor: '#ffffff',
        })
    }, [updateAppHeaderStyles])

    useEffect(() => {
        if (currentUserInfoResponse) {
            updateAppHeaderContent(
                `Product Listings (${productCount ?? 0})`,
                <ProductHeader
                    roles={{
                        isDeltekAdmin,
                    }}
                    firmId={
                        currentUserInfoResponse.currentUserInfo.account
                            .accountId
                    }
                    showAddProductButton={isDeltekAdmin}
                    loading={false}
                    showCompanyButton={isBPM}
                    onMenuChange={handleViewMenuChange}
                    onCompanyButtonClick={handleCompanyRoute}
                />
            )
        }
    }, [
        updateAppHeaderContent,
        currentUserInfoResponse,
        searchLoading,
        productCount,
        handleViewMenuChange,
        handleCompanyRoute,
        isDeltekAdmin,
        isBPM,
    ])

    useEffect(() => {
        if (!autosuggestLoading) {
            const autosuggestValues =
                autosuggestData?.listingAutoSuggest?.result || []
            const excludedListingLevels = ['Premium MasterSpec', 'Premium']
            setFilterValues({
                categories:
                    (resultFacetLookup as Record<
                        string,
                        string[]
                    >)?.Categories?.sort() ?? ProductCategories,
                listingLevels:
                    processEnv.REACT_APP_EnableBadgelessCards === '1'
                        ? ProductListingLevels.filter(
                            (item) => !excludedListingLevels.includes(item)
                        )
                        : ProductListingLevels.filter(
                            (item) => item !== 'Product Listing'
                        ),
                displayNameWithFirmName:
                    currentDropDownName === 'displayNameWithFirmName'
                        ? autosuggestValues
                        : [],
                sectionNames:
                    currentDropDownName === 'sectionNames'
                        ? autosuggestValues
                        : [],
                sectionNameWithProductType:
                    currentDropDownName === 'sectionNameWithProductType'
                        ? autosuggestValues
                        : [],
            })
        }
    }, [
        autosuggestData,
        autosuggestLoading,
        currentDropDownName,
        resultFacetLookup,
    ])

    useEffect(() => {
        if (!currentFirmNameInfo && accountId && isBPM)
            getFirmNameInfo({
                variables: {
                    firmId: accountId,
                },
            })
    }, [
        isBPM,
        currentUserInfoResponse,
        currentFirmNameInfo,
        getFirmNameInfo,
        location,
        accountId,
    ])

    // Used to reset the scrollbar
    useEffect(() => {
        if (hasMountedRef.current) {
            if (infiniteLoaderRef) {
                const list = infiniteLoaderRef.current as any
                if (list) {
                    const grid = list._listRef as FixedSizeGrid
                    list.resetloadMoreItemsCache() //Reset infinite scroll
                    grid.scrollToItem({
                        align: 'start',
                        columnIndex: 0,
                        rowIndex: 0,
                    }) //scroll to top after refresh
                    list.scrollTop = 0 //firefox solution for scroll not reseting
                }
            }
        }
        hasMountedRef.current = true
    }, [refetch, searchLoading])

    // Update current total based on cache
    useEffect(() => {
        if (
            !searchLoading &&
            !searchFetchMoreLoading &&
            !refetch &&
            !!searchData?.listingSearch?.matches
        ) {
            const count =
                searchData?.listingSearch?.matches?.length < PER_PAGE
                    ? productCount ?? 0
                    : searchData?.listingSearch?.matches?.length

            setCurrentTotal(count)

            setHasNextPage(
                count >= PER_PAGE
                    ? (listingMatchDataResults ?? [])?.length <
                    (searchData?.listingSearch?.count || 0)
                    : false
            )
        }
    }, [
        searchData,
        searchLoading,
        searchFetchMoreLoading,
        currentTotal,
        refetch,
        productCount,
        listingMatchDataResults,
    ])

    useEffect(() => {
        updateStateFromURL()
    }, [updateStateFromURL])

    const lastGroupingInfoRef = useRef<Record<string, boolean | string>>()

    const handleLoadMoreResults = useCallback(() => {
        if (getListings && skip < productCount) {
            const nextSkip = skip + PER_PAGE
            setSearchLoading(true)

            getListings({
                variables: {
                    manufacturerProductSearchInput: {
                        ...manufacturerProductSearchInput,
                        facets: normalizeFacets(
                            manufacturerProductSearchInput.facets
                        ),
                        skip,
                        top: PER_PAGE,
                    },
                },
            })
            setSkip(nextSkip)
        }

        if (!Object.values(groupBy).includes('spLoadMore'))
            lastGroupingInfoRef.current = groupBy

        let modifiedGroupBy: Record<string, boolean | string> = {}

        Object.keys(groupBy).forEach((key) => {
            modifiedGroupBy[key] = 'spLoadMore'
        })

        if (!isEqual(groupBy, modifiedGroupBy)) {
            setCardGroupBy(modifiedGroupBy)
        }
    }, [
        getListings,
        skip,
        manufacturerProductSearchInput,
        groupBy,
        productCount,
    ])

    useEffect(() => {
        if (
            !searchLoading &&
            accordionTreeRef &&
            !isTableView &&
            listingMatchDataResults?.length
        ) {
            const accordionTree = accordionTreeRef?.current
            const accordionTreeChild = accordionTree?.firstElementChild as HTMLElement

            if (accordionTreeChild) {
                accordionTreeChild.setAttribute('tabIndex', '0')
                accordionTreeChild.focus()
            }
        }
    }, [
        searchLoading,
        accordionTreeRef.current,
        isTableView,
        listingMatchDataResults,
    ])

    // Add the Group columns to the visible columns
    useEffect(() => {
        const groupingColumn = visibleColumns?.filter((col) =>
            memoizedTableGrouping?.find(
                (grouping: IdTitleProps) => grouping.id === col.id
            )
        )

        if (groupingColumn?.length !== memoizedTableGrouping?.length) {
            const columnsToAdd = memoizedTableGrouping.map(
                (grouping: IdTitleProps) => {
                    return tableColumns?.find((col) => col.id === grouping.id)
                }
            ) as TVColumn[]

            setVisibleColumns((past) => {
                let tempColumns = [
                    ...past.filter(
                        (col) =>
                            !columnsToAdd
                                .map((col) => col.accessor)
                                .includes(col.accessor)
                    ),
                    ...columnsToAdd.map((column) => renderColumn(column)),
                ]

                if (isBPMReviewer) {
                    tempColumns = tempColumns.filter(
                        (col) => col.id !== 'isActive'
                    )
                }

                return tempColumns
            })
        }
    }, [visibleColumns, memoizedTableGrouping, tableColumns, isBPMReviewer])

    //#endregion

    // This is for the Pills Filter
    useMemo(() => {
        let computedFacets = createFilter(facets, 'Search')

        const computedOrderBy = selectedValues?.orderBy?.length
            ? selectedValues.orderBy.join(',')
            : defaultSortOrder

        if (
            !isEqual(computedFacets, prevFacetsSearchRef?.current) ||
            !isEqual(computedOrderBy, prevOrderBySearchRef?.current) ||
            !isEqual(
                productTypeSelections,
                prevProductTypeSelectionsRef?.current
            ) ||
            !isEqual(
                prevSelectedAdditionalContent.current,
                selectedValues?.['additionalContent'] ?? []
            ) ||
            (!isEqual(prevSelectedInputValuesRef.current, selectedInputValues) &&
                selectedInputValues?.lastUpdatedInPastDays !== '')
        ) {
            tableRef?.current?.resetSelectedRows?.()
            prevProductTypeSelectionsRef.current = productTypeSelections
            setManufacturerProductSearchInput((past) => ({
                ...past,
                orderBy: computedOrderBy,
                facets: composeFacets(computedFacets, !isTableView),
                lastUpdatedEndDate: selectedInputValues?.lastUpdatedEndDate
                    ? selectedInputValues.lastUpdatedEndDate + 'T23:59:59.999Z'
                    : undefined,
                lastUpdatedStartDate: selectedInputValues?.lastUpdatedStartDate
                    ? selectedInputValues.lastUpdatedStartDate +
                    'T00:00:00.000Z'
                    : undefined,
                lastUpdatedInPastDays: selectedInputValues?.lastUpdatedInPastDays
                    ? 180
                    : undefined,
                hasBimLinks: selectedValues
                    ? isAdditionalContentSelected(
                        selectedValues['additionalContent'],
                        'hasBimLinks'
                    )
                    : undefined,
                hasCatalogs: selectedValues
                    ? isAdditionalContentSelected(
                        selectedValues['additionalContent'],
                        'hasCatalogs'
                    )
                    : undefined,
                hasDocumentFL: selectedValues
                    ? isAdditionalContentSelected(
                        selectedValues['additionalContent'],
                        'hasDocumentFL'
                    )
                    : undefined,
                hasDocumentSF: selectedValues
                    ? isAdditionalContentSelected(
                        selectedValues['additionalContent'],
                        'hasDocumentSF'
                    )
                    : undefined,
                productTypeSelections: productTypeSelections.map(
                    (each) =>
                    ({
                        productTypeProperties: each.productTypeProperties,
                        sectionName: each.sectionName,
                        productTypeName: each.productTypeName,
                    } as ProductTypeSelectionInput)
                ),
            }))
            setSkip(PER_PAGE)
            setRefetch(true)
            setCurrentTotal(10)
            prevSelectedAdditionalContent.current =
                selectedValues?.['additionalContent'] ?? []
            prevSelectedInputValuesRef.current = selectedInputValues
        }
    }, [
        facets,
        selectedValues,
        productTypeSelections,
        selectedInputValues,
        currentUserInfoResponse,
        accountType,
        location,
        isTableView,
    ])

    const createFacetLookup = (dataFacets: Partial<SearchFacet>[]) => {
        return dataFacets.reduce((result, facet) => {
            result[facet.name ?? ''] =
                facet?.values?.map((fv) => fv.value || '') || []

            return result
        }, {} as Record<string, string[]>)
    }

    // Useeffect for calling the API when the filters change
    useEffect(() => {
        if (
            isTableView &&
            isTableSettingsLoaded &&
            isPreviousTableSettingsLoaded &&
            (!isEqual(prevFilters?.current, filtersState) ||
                !isEqual(prevOrderBy?.current, orderByState))
        ) {
            const orderByStateArray = [
                ...new Set(
                    orderByState
                        .replace('sectionName', 'sectionNumber')
                        .replace('familyNumber', 'sectionNumber')
                        .split(',')
                ),
            ] as string[]

            const validOrderByFiltered = orderByStateArray.filter(
                (orderBy: string) => {
                    return validOrderBy.includes(orderBy.split(' ')[0])
                }
            )

            let newManufacturerProductSearchInput = {
                ...manufacturerProductSearchInput,
            }

            setManufacturerProductSearchInput((past) => {
                newManufacturerProductSearchInput = {
                    ...newManufacturerProductSearchInput,
                    facets: composeFacets(past.facets),
                    orderBy: validOrderByFiltered.join(','),
                }

                if (!isEqual(newManufacturerProductSearchInput, past)) {
                    getListings({
                        variables: {
                            manufacturerProductSearchInput: {
                                ...newManufacturerProductSearchInput,
                                facets: normalizeFacets(
                                    newManufacturerProductSearchInput.facets
                                ),
                                skip: 0,
                            },
                        },
                    })
                    return newManufacturerProductSearchInput
                }
                return past
            })

            prevOrderBy.current = orderByState
            prevFilters.current = filtersState
        }
    }, [
        composeFacets,
        filtersState,
        orderByState,
        prevOrderBy?.current,
        prevFilters?.current,
        isTableView,
        isTableSettingsLoaded,
        manufacturerProductSearchInput,
        isPreviousTableSettingsLoaded,
    ])

    useEffect(() => {
        if (
            isTableSettingsLoaded &&
            !isPreviousTableSettingsLoaded &&
            [...selectedColumnsState, ...tableGrouping].length > 0
        ) {
            if (!!listingMatchDataResults?.length) {
                let tempColumns = [
                    ...selectedColumnsState.map((col: IdTitleProps) => {
                        return {
                            ...(columnMap[col.id] as SpecColumn<ListingMatch>),
                            width: columnWidthsState?.[col.id] ?? 300,
                        }
                    }),
                ].map((column: TVColumn) => {
                    return renderColumn(column)
                })

                if (isBPMReviewer) {
                    tempColumns = tempColumns.filter(
                        (col) => col.id !== 'isActive'
                    )
                }

                setVisibleColumns(tempColumns)

                setIsPreviousTableSettingsLoaded(true)
                setHiddenColumns([
                    ...Object.keys(columnMap).filter(
                        (col) =>
                            !selectedColumnsState
                                .map((col: IdTitleProps) => col.id)
                                .includes(col)
                    ),
                ])
            }
        }
    }, [
        collapseColumnCheckedState,
        history,
        isTableSettingsLoaded,
        isPreviousTableSettingsLoaded,
        selectedColumnsState,
        columnWidthsState,
        tableGrouping,
        orderByState,
        searchData,
        listingMatchDataResults,
        selectedValues,
        searchLoading,
        fetchNotes,
        isBPMReviewer,
    ])

    //on refetch change

    useEffect(() => {
        const expandedRows = getExpandedRows(
            listingMatchDataResults,
            tableGrouping.reduce(
                (acc: Record<string, boolean>, curr: IdTitleProps) => {
                    acc[curr.id] = collapseColumnCheckedState[curr.id]
                    return acc
                },
                {}
            )
        )

        setDefaultExpandedRows(expandedRows)
        setAutoResetExpanded(true)
    }, [
        listingMatchDataResults,
        collapseColumnCheckedState,
        getExpandedRows,
        tableGrouping,
    ])

    useEffect(() => {
        if (refetch) {
            doRefetch()
        }
    }, [doRefetch, refetch])

    // add notes in the default hidden since user is admin. Default has no PDN filter
    useEffect(() => {
        if (!!isAdminAccess) {
            setHiddenColumns(adminColumnKeys)
        }
    }, [])

    // onRender
    useEffect(() => {
        doRefetch()
        fetchTableSettings()
        setSelectedRowIds?.({})
        tableRef?.current?.setAllFilters?.([])
    }, [])

    return (
        <GridContainer className={classes.container} direction="column">
            <GridItem
                className={classes.productSearchContOuter}
                data-testid="productListingsPage"
            >
                <ProductSearchBar
                    filterValues={filterValues}
                    filterSelection={{
                        ...selectedValues,
                    }}
                    filterDisabledValues={disabledFilterValues}
                    loadingFilterName={
                        autosuggestLoading ? currentDropDownName : ''
                    }
                    currentUserInfoResponse={currentUserInfoResponse}
                    emptyInputSelection={selectedInputValues}
                    productTypeSelections={productTypeSelections}
                    resultCount={productCount}
                    loadingSelectedProductType={loadingSelectedProductType}
                    errorSelectedProductType={errorSelectedProductType}
                    dataSelectedProductType={dataSelectedProductType}
                    getListingProductTypeProperties={
                        getListingProductTypeProperties
                    }
                    showProductSortingModal={!isTableView}
                    onSelected={onFilterSelected}
                    setOnSelected={setOnFilterSelected}
                    clearAllHandler={handleClearAll}
                    onFilterSelectionChange={onFilterSelectionChangeHandler}
                    onFilterInputChangeDebounced={
                        handleFilterInputChangeDebounced
                    }
                    handelProductTypeSelection={handleProductTypeSelection}
                    handleAutoSuggestDropdown={handleAutoSuggestDropdown}
                    handleEmptyInputSelection={handleEmptyInputSelection}
                    pdnTypes={pdnTypes}
                    getNoteTypeReasonOptionFromString={
                        getNoteTypeReasonOptionFromString
                    }
                    showSwatchboxFilter={
                        !!Number(processEnv.REACT_APP_ShowSwatchbox)
                    }
                    showNotesFilter={isAdminAccess}
                />
            </GridItem>
            <GridItem className={classes.specTableContainer}>
                <SpecTableTitleSettings
                    tableViewActive={isTableView}
                    showFiltersRow={isTableView && tableFilterView}
                    showTableSettings={isTableView}
                    tableSettings={{
                        maxGroupCount: 3,
                        showGroupOptionSetting: true,
                        showPinning: true,
                        showCollapseToggle: true,
                        columns: tableColumns as any,
                        visibleColumns: visibleColumns as any,
                        initialHiddenColumnIds: hiddenColumns as string[],
                        onTableSettingsChange: setPinningProps,
                        setColumnOrder: handleTableOrder,
                        setHiddenColumns: handleColumnRemove,
                        setCollapsedGroupColumns: handleCollapseColumnState,
                    }}
                    theme="light"
                    isBulkActionsEnabled={isBulkActionsButtonEnabled}
                    onBulkActionsClick={handleBulkActions}
                    onFilterClick={handleCardViewFilterClick}
                    onExportClick={handleOpenExportModal}
                    onTableViewClick={handleViewToggle(true)}
                    onCardViewClick={handleViewToggle(false)}
                    showBulkActionsButton={
                        isDeltekAdmin &&
                        processEnv.REACT_APP_ShowBulkActionsButton === '1'
                    }
                    saveTableViewSettings={saveTableViewSettings}
                />
                <ResultsExport
                    handleClose={handleCloseExportModal}
                    open={openExportModal}
                    tableGrouping={tableGrouping}
                    selectedColumns={selectedColumnsState}
                    columnWidths={columnWidthsState}
                    orderBy={memoizedOrderByString}
                    manufacturerProductSearchInput={
                        manufacturerProductSearchInput
                    }
                    assignedCompany={assignedCompany}
                    isAdminAccess={isAdminAccess}
                    totalCount={productCount}
                    normalizeFacets={normalizeFacets}
                ></ResultsExport>
            </GridItem>
            <GridItem className={classes.productContent}>
                {!isTableView ? (
                    <>
                        {!Object.keys(groupBy).length ? (
                            <ProductListingResultGrid
                                searchLoading={searchLoading}
                                searchFetchMoreLoading={searchFetchMoreLoading}
                                products={listingMatchDataResults}
                                currentTotal={currentTotal}
                                hasNextPage={hasNextPage}
                                isItemLoaded={isItemLoaded}
                                scrollState={scrollState}
                                setScrollRowAndColumn={setScrollRowAndColum}
                                infiniteLoaderRef={infiniteLoaderRef}
                                setColumnCount={setColumnCount}
                                handleLoadMore={handleLoadMore}
                            />
                        ) : !searchLoading &&
                            listingMatchDataResults?.length === 0 ? (
                            <GridContainer
                                spacing={1}
                                className={clsx(
                                    'divProductListingNoResultGrid',
                                    classes.resultContainer
                                )}
                                justifyContent="center"
                                alignContent="space-between"
                            >
                                <GridItem>
                                    <h3>No Results</h3>
                                </GridItem>
                            </GridContainer>
                        ) : (
                            !!listingMatchDataResults?.length && (
                                <AutoSizer
                                    className="accordionTree"
                                    data-testid="accordion-tree"
                                >
                                    {({ height, width }) => (
                                        <AccordionWindow
                                            accordionTreeRef={accordionTreeRef}
                                            listingCount={productCount}
                                            listings={sortedProductList}
                                            searchLoading={searchLoading}
                                            groupBy={groupBy}
                                            height={height}
                                            width={width}
                                            listingLoading={listingLoading}
                                            onLoadMore={handleLoadMoreResults}
                                            isDeltekUser={isDeltekUser}
                                        />
                                    )}
                                </AutoSizer>
                            )
                        )}
                    </>
                ) : (
                    <>
                        <TablePage
                            tableData={sortedProductList}
                            tableDataNetworkStatus={listingNetworkStatus}
                            totalItems={productCount ?? 0}
                            showFiltersRow={tableFilterView}
                            columns={visibleColumns}
                            hiddenColumns={hiddenColumns as string[]}
                            groupBy={memoizedGroupIds}
                            fetchMoreTableData={handleLoadMoreResults}
                            defaultExpandedRows={defaultExpandedRows}
                            setAutoResetExpanded={setAutoResetExpanded}
                            autoResetExpanded={autoResetExpanded}
                            tableRef={tableRef}
                            fetchDataLoading={
                                showTableLoadingIndicator || listingLoading
                            }
                            resetFetch={resetFetch}
                            fetchNextDataLoading={listingLoading}
                            showBottomLoadingIndicator={
                                showBottomLoadingIndicator
                            }
                            isAdminAccess={isAdminAccess}
                        ></TablePage>
                        <ListingNotesModal
                            isOnEditMode={false}
                            open={isNotesDialogOpen && !pdnLoading}
                            onClose={handleCloseNotesModal}
                            isManufacturerListing={
                                listingLevel === ListingLevel.Manufacturer
                            }
                            filterFromQuery={true}
                        />
                        <LoadingBackdrop isVisible={pdnLoading} />
                    </>
                )}
            </GridItem>
            <BulkCopyMoveDialog
                isOpen={isBulkCopyMoveOpen}
                setIsOpen={setIsBulkCopyMoveOpen}
                type={bulkActionType}
                setType={setBulkActionType}
            />
            <BulkProgressStatusDialog
                resetSelect={resetSelectedRowsThenRefetch}
            />
            <ProductListingLoading />
        </GridContainer>
    )
}

export default ProductListingsPage
